import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import type { Message } from "@ai-sdk/react"

export interface ChatMessagesContextType {
  messages: Message[]
  addMessage: (message: Message) => void
  removeMessage: (messageId: string) => void
  updateMessage: (messageId: string, updates: Partial<Message>) => void
  clearMessages: () => void
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void
}

const ChatMessagesContext = createContext<ChatMessagesContextType | undefined>(undefined)

interface ChatMessagesProviderProps {
  children: ReactNode
  initialMessages?: Message[]
}

export function ChatMessagesProvider({ 
  children, 
  initialMessages = [] 
}: ChatMessagesProviderProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)

  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message])
  }, [])

  const removeMessage = useCallback((messageId: string) => {
    setMessages(prev => prev.filter(m => m.id !== messageId))
  }, [])

  const updateMessage = useCallback((messageId: string, updates: Partial<Message>) => {
    setMessages(prev => 
      prev.map(message => 
        message.id === messageId 
          ? { ...message, ...updates }
          : message
      )
    )
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  const value: ChatMessagesContextType = {
    messages,
    addMessage,
    removeMessage,
    updateMessage,
    clearMessages,
    setMessages
  }

  return (
    <ChatMessagesContext.Provider value={value}>
      {children}
    </ChatMessagesContext.Provider>
  )
}

export function useChatMessages() {
  const context = useContext(ChatMessagesContext)
  if (context === undefined) {
    throw new Error("useChatMessages must be used within a ChatMessagesProvider")
  }
  return context
} 